import { forwardRef, useEffect } from "react";
import type { HTMLAttributes } from "react";
import { X } from "lucide-react";

interface ModalProps extends HTMLAttributes<HTMLDivElement> {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  maxWidth?: "sm" | "md" | "lg" | "xl" | "2xl" | "full";
  children: React.ReactNode;
}

const Modal = forwardRef<HTMLDivElement, ModalProps>(
  (
    {
      isOpen,
      onClose,
      title,
      maxWidth = "2xl",
      children,
      className = "",
      ...props
    },
    ref
  ) => {
    // Handle ESC key press
    useEffect(() => {
      const handleEscape = (e: KeyboardEvent) => {
        if (e.key === "Escape" && isOpen) {
          onClose();
        }
      };

      if (isOpen) {
        document.addEventListener("keydown", handleEscape);
        // Prevent body scroll when modal is open
        document.body.style.overflow = "hidden";
      }

      return () => {
        document.removeEventListener("keydown", handleEscape);
        document.body.style.overflow = "unset";
      };
    }, [isOpen, onClose]);

    if (!isOpen) return null;

    const maxWidthClasses = {
      sm: "max-w-sm",
      md: "max-w-md",
      lg: "max-w-lg",
      xl: "max-w-xl",
      "2xl": "max-w-2xl",
      full: "max-w-full",
    };

    return (
      <div
        className="fixed inset-0 z-50 flex items-center justify-center p-4"
        style={{ fontFamily: "'Courier New', monospace" }}
      >
        {/* Backdrop */}
        <div
          className="absolute inset-0 bg-black bg-opacity-70 transition-opacity"
          onClick={onClose}
        />

        {/* Modal Content */}
        <div
          ref={ref}
          className={`relative bg-white border-4 border-black shadow-[12px_12px_0px_0px_rgba(0,0,0,1)] ${maxWidthClasses[maxWidth]} w-full max-h-[90vh] overflow-hidden flex flex-col ${className}`}
          onClick={(e) => e.stopPropagation()}
          {...props}
        >
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b-4 border-black">
            {title && (
              <h2 className="text-2xl md:text-3xl font-bold text-black">
                {title}
              </h2>
            )}
            <button
              onClick={onClose}
              className="ml-auto p-2 hover:bg-gray-100 transition-colors border-2 border-black"
              aria-label="Close modal"
            >
              <X className="w-6 h-6 text-black" />
            </button>
          </div>

          {/* Body - Scrollable */}
          <div className="flex-1 overflow-y-auto p-6">{children}</div>
        </div>
      </div>
    );
  }
);

Modal.displayName = "Modal";

export default Modal;
